<template>
  <a
    :class="['fact-finder-item', { 'fact-finder-item--fite': isFite }]"
    data-test="search-result-item"
    :href="href"
    @click="onClick"
  >
    <div
      v-if="bestPrice?.price?.savingsInformation?.savingsInPercent"
      class="fact-finder-item__savings"
    >
      {{
        $dict.get('online-Shop.Product.Savings', [
          bestPrice.price.savingsInformation.savingsInPercent,
        ])
      }}
    </div>

    <div
      v-if="!isPage"
      class="fact-finder-item__image"
    >
      <ssr-cloud-image-fi
        :src="imageUrl || placeholderImage"
        :aspect-ratio="isFite ? 1 : aspectRatio"
        :unlimited="isFite ? 248 : unlimited"
        :breakpoints="isFite ? fiteBreakpoints : breakpoints"
        :alt="itemName"
        params="func=fit"
        trim
        do-not-replace-url
      />
    </div>

    <div
      v-if="record?.masterValuesObject?.catalogSeriesData?.CategoryName"
      class="fact-finder-item__category"
      data-test="search-result-item-category"
    >
      {{ record.masterValuesObject.catalogSeriesData.CategoryName }}
    </div>

    <!-- eslint-disable vue/no-v-html -->
    <div
      class="fact-finder-item__name"
      data-test="search-result-item-name"
      v-html="highlightName"
    />
    <!-- eslint-enable vue/no-v-html -->
    <div
      v-if="isProduct"
      class="fact-finder-item__article-nr"
      data-test="article-number"
    >
      {{ $dict.get('product-List.Table.Article-Number') }}
      {{ variant.ID }}
    </div>

    <div
      v-if="isFamily"
      :class="{
        'fact-finder-item__family': true,
        'fact-finder-item__family--has-description': showDescription,
      }"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="showDescription"
        class="fact-finder-item__description"
        v-html="
          record?.masterValuesObject?.catalogSeriesData?.MarketingClaim ||
          record?.masterValuesObject?.catalogCategoryData?.MarketingClaim
        "
      />
      <!-- eslint-enable vue/no-v-html -->

      <div
        v-if="record.variantCount"
        class="fact-finder-item__family-variants"
        data-test="search-result-item-variants-count"
      >
        {{
          $dict.get(
            record.variantCount === 1 ? 'search.Variants-Singular' : 'search.Variants-Plural',
            [String(record.variantCount)],
          )
        }}
      </div>
    </div>
    <div
      v-else-if="isProduct && (isFite || (bestPrice && !bestPrice.price?.loading))"
      class="fact-finder-item__product-info"
    >
      <template v-if="isFite">
        <icon-attribute-item-fi
          v-if="attributeValue(variantAttributes, 'ALTER_AB')"
          class="fact-finder-item__recommended-age"
          icon="age"
          :size="1"
          :value="attributeValue(variantAttributes, 'ALTER_AB')"
          :label="attributeName(variantAttributes, 'ALTER_AB')"
        />
        <div
          v-if="variantProductPrice || shopifyPrice"
          class="fact-finder-item__uvp-price"
        >
          <div class="fact-finder-item__uvp-price-value">
            <loading-indicator
              v-if="shopifyLoading"
              variant="spinnerLocal"
            />
            <template v-if="shopifyPrice">
              <div
                class="fact-finder-item__shopify-price"
                :class="{ 'fact-finder-item__shopify-price-right': !hasAlterAB }"
              >
                <div
                  v-if="compareAtPrice"
                  class="fact-finder-item__compare-price-value"
                >
                  {{ compareAtPrice }}
                </div>
                <div class="fact-finder-item__shopify-price-value">{{ shopifyPrice }}</div>
              </div>
            </template>
            <template v-else>
              {{ `${uvpPricePrefix} ${currentProductPrice}` }}
            </template>
          </div>
          <div class="fact-finder-item__uvp-price-vat">
            {{ $dict.get('online-Shop.Product.Vat-included') }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="fact-finder-item__best-price">
          <div class="fact-finder-item__best-price-price">
            <span
              v-if="bestPrice?.price?.savingsInformation"
              class="fact-finder-item__stroke-price"
            >
              {{ bestPrice.price.savingsInformation.totalStrokePriceValueFormatted }}
            </span>
            <span class="fact-finder-item__best-price-value">
              {{ bestPrice?.price?.valueFormatted }}
            </span>
          </div>
          <div class="fact-finder-item__best-price-vat">
            {{ $dict.get('online-Shop.Product.Vat-excluded') }}
          </div>
        </div>
      </template>
    </div>
    <div
      v-else-if="isPage"
      class="fact-finder-item__page"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="fact-finder-item__texts"
        v-html="highlightText"
      />
      <!-- eslint-enable vue/no-v-html -->
      <div class="fact-finder-item__path">
        {{
          $dict.get('search.Found-In', [
            record.variantValuesObject?.[0]?.CategoryPathName?.[0]?.join(' | ') || '',
          ])
        }}
      </div>
      <arrow-icon class="fact-finder-item__arrow" />
    </div>
  </a>
</template>

<script setup lang="ts">
import { ArrowIcon, IconAttributeItemFi, LoadingIndicator, SsrCloudImageFi } from 'atoms';
import { storeToRefs } from 'pinia';
import { computed, inject } from 'vue';

import type { BreadCrumbItem } from '@/components/atoms/breadcrumb-fi/BreadcrumbFi.vue';
import { useAssetUrl } from '@/composables/assetUrl';
import type { BestPrice } from '@/composables/bestPrice';
import { useCatalogAttributes } from '@/composables/catalogAttributes';
import { useEventHub } from '@/composables/eventHub';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { usePriceCurrency } from '@/composables/priceCurrency';
import { useShopifyProduct } from '@/composables/shopifyProduct';
import type { SearchFactFinderNgQuery } from '@/generated/graphql';
import { useNavigationStore } from '@/store/pinia/navigation';
import { useSearchStore } from '@/store/pinia/search';
import { useShopifyStore } from '@/store/pinia/shopify';

import { resolveItemProduct } from './itemProduct';

defineOptions({
  name: 'FactFinderItemFi',
});

const getSearchData = inject<
  () => {
    query: string;
    pageSize: number;
    attachBreadcrumb: boolean;
    clickSource: string;
  }
>('getSearchData');

type FactFinderMasterRecord = NonNullable<
  NonNullable<NonNullable<SearchFactFinderNgQuery['searchFactFinderNg']>['masters']>['hits'][number]
>;

type FactFinderVariantRecord = NonNullable<
  NonNullable<
    NonNullable<NonNullable<SearchFactFinderNgQuery['searchFactFinderNg']>['variants']>['hits']
  >[number]
>;

interface RecordEnhancements {
  masterValuesObject?: {
    masterProductData?: {
      bestPrice?: BestPrice | null;
    } | null;
  } | null;
  variantValuesObject?: Array<{
    Texts?: string | null;
  } | null> | null;
}

type FactFinderRecord = FactFinderMasterRecord & FactFinderVariantRecord & RecordEnhancements;

const props = defineProps<{
  record: FactFinderRecord;
  bestPrice?: (BestPrice & { price?: { loading?: boolean } | null }) | null;
  filterUrl?: string;
  showDescription: boolean;
  type: string;
  recordIdx: number;
}>();

const fischer = useFischer();
const navigationStore = useNavigationStore();
const searchStore = useSearchStore();
const dict = useDict();
const { sendClickToEventHub } = useEventHub();
const { getAssetUrl } = useAssetUrl();
const shopifyStore = useShopifyStore();
const { shopifyProducts, loading: shopifyLoading } = storeToRefs(shopifyStore);
const { formatPrice } = usePriceCurrency();
const { getFormattedPrice, getFormattedCompareAtPrice } = useShopifyProduct(shopifyProducts);
const { attributeName, attributeValue } = useCatalogAttributes();

const aspectRatio = 19 / 15;
const unlimited = 220;

const breakpoints = [
  {
    mediaWidth: 369,
    imageWidth: 261,
  },
  {
    mediaWidth: 767,
    imageWidth: 220,
  },
  {
    mediaWidth: 1023,
    imageWidth: 233,
  },
  {
    mediaWidth: 1439,
    imageWidth: 255,
  },
];

const fiteBreakpoints = [
  {
    mediaWidth: 369,
    imageWidth: 285,
  },
  {
    mediaWidth: 479,
    imageWidth: 395,
  },
  {
    mediaWidth: 767,
    imageWidth: 318,
  },
];

const placeholderImage = getAssetUrl('/img/not-found.png');

const shopifyPrice = getFormattedPrice(props.record.idString || '');
const compareAtPrice = getFormattedCompareAtPrice(props.record.idString || '');

const variant = computed(() => resolveItemProduct(props.record, props.type));

const isProduct = computed(
  () =>
    props.type === 'product' ||
    Boolean(props.record?.masterValuesObject?.masterProductData?.catalogProductData),
);

const isFamily = computed(
  () => (props.type === 'family' || props.type === 'series') && !isProduct.value,
);

const isPage = computed(() => props.type === 'page');

const imageUrl = computed(() => {
  if (isFamily.value) {
    if (props.record?.masterValuesObject?.catalogSeriesData?.imageUrl) {
      return props.record?.masterValuesObject.catalogSeriesData.imageUrl;
    }
    if (props.record?.masterValuesObject?.catalogCategoryData?.imageUrl) {
      return props.record?.masterValuesObject.catalogCategoryData.imageUrl;
    }
  }

  if (isProduct.value) {
    return variant.value?.ImageURL;
  }

  return null;
});

const href = computed(() => {
  const href = [];
  if (isFamily.value || isProduct.value) {
    href.push(fischer.pages.products().url);
  }

  if (isFamily.value) {
    if (props.record?.variantCount === 1 && props.record?.singleVariantCatalogData?.Slug) {
      href.push(props.record.singleVariantCatalogData?.Slug);
    } else {
      href.push(
        props.record?.masterValuesObject?.catalogSeriesData?.Slug ||
          props.record?.masterValuesObject?.catalogCategoryData?.Slug,
        props.filterUrl,
      );
    }
  } else {
    href.push(variant.value.Slug);
  }
  return href.join('');
});

const highlightText = computed(() => {
  const markedText = markText(props.record?.variantValuesObject?.[0]?.Texts || '');
  return shortenText(markedText);
});

const itemName = computed(() => {
  if (isFamily.value) {
    return (
      props.record?.masterValuesObject?.catalogSeriesData?.Name ||
      props.record?.masterValuesObject?.catalogCategoryData?.CategoryName ||
      // @ts-expect-error I do not know where this comes from
      props.record?.masterValuesObject?.masterProductData?.catalogProductData?.categoryData
        ?.CategoryName
    );
  }
  return variant.value?.Name || '';
});

const highlightName = computed(() => {
  if (!isPage.value) {
    return escapeChars(itemName.value);
  }

  return markText(itemName.value);
});

const variantAttributes = computed((): any[] => (variant.value as any)?.Attributes ?? []);
const variantProductPrice = computed(() => (variant.value as any)?.ProductPrice);
const variantProductPriceCurrency = computed(
  (): string => (variant.value as any)?.ProductPriceCurrency ?? '',
);
const hasAlterAB = computed(() =>
  variantAttributes.value.some((attr: any) => attr.AttributeKey === 'ALTER_AB'),
);
const uvpPricePrefix = computed(() => dict.get('online-Shop.Product.UVP-Price-Prefix') || '');
const currentProductPrice = computed(() =>
  formatPrice(variantProductPrice.value ?? 0, variantProductPriceCurrency.value),
);

const markText = (text: string) => {
  const escapedText = escapeChars(text);

  const queryValue = searchStore.query;
  const words = escapeChars(queryValue)
    .split(' ')
    .filter((x) => x.length > 2);
  if (!words.length) {
    return escapedText;
  }

  const regexSafeWords = words.map((word) =>
    // eslint-disable-next-line no-useless-escape
    word.replace(/[\[\\^$.|?*+()]/g, ([char]) => `\\${char}`),
  );
  const wordsRegex = new RegExp(regexSafeWords.join('|'), 'gi');
  const markedText = escapedText.replace(wordsRegex, (word) => `<mark>${word}</mark>`);

  return markedText;
};

const shortenText = (text: string) =>
  text.replace(/^[^<]+</, (lead) => {
    const shortLead = / [^ ]{0,15}.{15}$/.exec(lead);
    return shortLead ? `...${shortLead[0]}` : lead;
  });

const escapeChars = (unescaped?: string) =>
  (unescaped || '').replace(/[<>&]/g, (char) => `&#${char.codePointAt(0) ?? 0};`);

const onClick = (event: MouseEvent) => {
  if (!getSearchData) {
    console.error('missing search data inject');
    return;
  }
  const { pageSize, clickSource, query, attachBreadcrumb } = getSearchData();
  let eventQuery;
  if (attachBreadcrumb) {
    const productBreadcrumb = navigationStore.productBreadcrumb;
    const breadcrumb = productBreadcrumb.map((item: BreadCrumbItem) => item.title);
    breadcrumb.unshift(query);
    breadcrumb.push(itemName.value);
    eventQuery = breadcrumb.join('/');
  } else {
    eventQuery = query;
  }
  sendClickToEventHub(
    {
      clickSource,
      familyId: props.type === 'family' ? props.record?.idString || undefined : undefined,
      seriesId: props.type === 'series' ? props.record?.idString || undefined : undefined,
      page: Math.max(1, Math.ceil(props.recordIdx / pageSize)),
      pageSize,
      pageTitle: itemName.value,
      position: props.recordIdx + 1,
      productId: isProduct.value ? variant.value?.ID : undefined,
      productTitle: isProduct.value ? itemName.value : null,
      query: eventQuery,
      score: props.record?.score,
      url: window.location.href,
    },
    event,
    href.value,
  );
};
</script>

<style scoped src="./fact-finder-item-fi" />
